import {
  analyzeMissingIngredientsOperation,
  scaleMealIngredientsOperation,
  updateFoodBrainDerivedDataOnMeal,
} from './meal_operations';
import { trackEvents } from './tracking_operations';
import { newNoteWithDefaultFields } from '../services/notes';
import {
  daysArrayIndex,
  dbWeekStartDatesAndDayIndexesCoveringCalendarRange,
  dbWeekStartDatesAndDayIndexesCoveringViewWeek,
  dbWeekStartDatesForDayIndexes,
  plannerWeekWithMealAdded,
  plannerWeekWithNoteAdded,
  plannerWeekWithObjectRemoved,
} from '../services/planner';
import { deduplicate } from '../services/arrays';
import { cloneObject } from './utils';
import {
  mealWithIngredientTokensUpdated,
  mealWithScaledIngredientsReplaced,
  newMealWithDefaultFields,
} from '../services/meals';
import { PlannerEntryType } from '../API';
import { mealsRemovedFromMealBasket } from '../features/end_user_shopping/end_user_shopping_slice';
import { objectify } from '../services/typed_arrays';

export const getOrCreatePlannerWeeks = async (
  dbWeekStartDates,
  lazyGetOrCreateDbPlannerWeekByStartDate,
) => {
  const promises = dbWeekStartDates.map(async (dbWeekStartDate) => {
    const { data: dbPlannerWeek } =
      await lazyGetOrCreateDbPlannerWeekByStartDate(dbWeekStartDate);
    return dbPlannerWeek;
  });
  const plannerWeeksList = await Promise.all(promises);
  return objectify(plannerWeeksList, 'startDate');
};

export const copyMealToPlanner = async (
  fullMeal,
  toPlannerViewWeekStartDate,
  dayIndexes,
  customerFoodDataID,
  tracker,
  userProfile,
  currentSpaceMembership,
  lazyGetOrCreateDbPlannerWeekByStartDate,
  createMeal,
  updateMeal,
  updateDbPlannerWeek,
  lazyGetMeal,
  scaleToServings,
) => {
  const dbWeekRecords = dbWeekStartDatesForDayIndexes(
    toPlannerViewWeekStartDate,
    dayIndexes,
  );
  const dbWeekStartDates = deduplicate(
    dbWeekRecords.map((dbwr) => dbwr.dbWeekStartDate),
  );
  const plannerWeeks = await getOrCreatePlannerWeeks(
    dbWeekStartDates,
    lazyGetOrCreateDbPlannerWeekByStartDate,
  );
  const updatedPlannerWeeks = { ...plannerWeeks };
  const currentHealthProGroup = null;
  const clonedMeal = cloneObject(fullMeal);
  let scaledClonedMeal = clonedMeal;
  if (scaleToServings !== undefined) {
    if (
      !clonedMeal.recipes[0].servings ||
      clonedMeal.recipes[0].servings <= 0
    ) {
      console.log('Will not scale meal with no servings');
    } else {
      const scaleFactor = scaleToServings / clonedMeal.recipes[0].servings;
      const scaledIngredients = await scaleMealIngredientsOperation(
        clonedMeal,
        scaleFactor,
        customerFoodDataID,
      );
      scaledClonedMeal = mealWithScaledIngredientsReplaced(
        clonedMeal,
        scaleToServings,
        scaledIngredients,
        scaleFactor,
      );
    }
  }
  try {
    const insertedMeals = await Promise.all(
      dbWeekRecords.map(async (dbwr) => {
        const plannerWeek = plannerWeeks[dbwr.dbWeekStartDate];
        const { data: insertedMeal } = await createMeal(
          newMealWithDefaultFields(
            scaledClonedMeal,
            plannerWeek.id,
            currentSpaceMembership?.id,
            currentHealthProGroup,
          ),
        );
        return insertedMeal;
      }),
    );
    dbWeekRecords.forEach((dbwr, i) => {
      updatedPlannerWeeks[dbwr.dbWeekStartDate] = plannerWeekWithMealAdded(
        updatedPlannerWeeks[dbwr.dbWeekStartDate],
        insertedMeals[i],
        dbwr.dayIndex,
      );
    });
    tracker(userProfile, ['Add to planner'], ['numMealsPlanned']);
    await Promise.all(
      insertedMeals.map(async (insertedMeal) =>
        updateFoodBrainDerivedDataOnMeal(
          insertedMeal.id,
          insertedMeal.recipes[0],
          insertedMeal.derivedNutrition?.ingredientNutrition,
          customerFoodDataID,
          lazyGetMeal,
          updateMeal,
        ),
      ),
    );
    return { updatedPlannerWeeks };
  } finally {
    await Promise.all(
      dbWeekStartDates.map((dbWeekStartDate) =>
        updateDbPlannerWeek(updatedPlannerWeeks[dbWeekStartDate]),
      ),
    );
  }
};

export const moveMealInPlanner = async (
  fullMeal,
  fromDbPlannerWeek,
  fromDayIndex,
  toPlannerViewWeekStartDate,
  dayIndexes,
  customerFoodDataID,
  tracker,
  userProfile,
  currentSpaceMembership,
  lazyGetOrCreateDbPlannerWeekByStartDate,
  createMeal,
  updateMeal,
  deleteMeal,
  updateDbPlannerWeek,
  lazyGetMeal,
) => {
  return copyMealToPlanner(
    fullMeal,
    toPlannerViewWeekStartDate,
    dayIndexes,
    customerFoodDataID,
    tracker,
    userProfile,
    currentSpaceMembership,
    lazyGetOrCreateDbPlannerWeekByStartDate,
    createMeal,
    updateMeal,
    updateDbPlannerWeek,
    lazyGetMeal,
  ).then(async ({ updatedPlannerWeeks: updatedDbPlannerWeeksAfterCopy }) => {
    // Delete this planner entry
    const updatedDbPlannerWeekAfterCopy =
      updatedDbPlannerWeeksAfterCopy[fromDbPlannerWeek.startDate] ||
      fromDbPlannerWeek;
    const updatedDbPlannerWeek = plannerWeekWithObjectRemoved(
      updatedDbPlannerWeekAfterCopy,
      fullMeal.id,
      fromDayIndex,
    );
    return Promise.all([
      deleteMeal({
        id: fullMeal.id,
        parentID: fromDbPlannerWeek.id,
      }),
      updateDbPlannerWeek(updatedDbPlannerWeek),
    ]);
  });
};

export const clearPlanner = async (
  fromCalendarDay,
  toCalendarDay,
  lazyGetOrCreateDbPlannerWeekByStartDate,
  dispatch,
  deleteMeal,
  deleteNote,
  deleteContentEntry,
  updatePlannerWeek,
) => {
  const dbWeekRecords = dbWeekStartDatesAndDayIndexesCoveringCalendarRange(
    fromCalendarDay,
    toCalendarDay,
  );
  const dbWeekStartDates = deduplicate(
    dbWeekRecords.map((dbwr) => dbwr.dbWeekStartDate),
  );
  const plannerWeeks = await getOrCreatePlannerWeeks(
    dbWeekStartDates,
    lazyGetOrCreateDbPlannerWeekByStartDate,
  );
  let updatedPlannerWeeksList = [];
  const promises = [];
  const mealBasketMealIDsToRemove = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const dbwr of dbWeekRecords) {
    console.log(
      `Clearing planner for DB week start date ${dbwr.dbWeekStartDate} day index ${dbwr.dayIndex}`,
    );
    const originalPlannerWeek = plannerWeeks[dbwr.dbWeekStartDate];
    if (!originalPlannerWeek) {
      console.warn(
        `Cannot find planner week for DB week start date ${dbwr.dbWeekStartDate}`,
      );
      return;
    }
    const updatedPlannerWeek = updatedPlannerWeeksList.find(
      (pw) => pw.id === originalPlannerWeek.id,
    ) || { ...originalPlannerWeek };

    const plannerDay = updatedPlannerWeek.days[daysArrayIndex(dbwr.dayIndex)];
    if (!plannerDay) {
      console.warn(
        `Cannot find planner day for DB week start date ${dbwr.dbWeekStartDate} day index ${dbwr.dayIndex}`,
      );
      return;
    }
    const updatedPlannerDay = { ...plannerDay, entries: [] };
    updatedPlannerWeek.days = [...updatedPlannerWeek.days];
    updatedPlannerWeek.days[daysArrayIndex(dbwr.dayIndex)] = updatedPlannerDay;
    updatedPlannerWeeksList = updatedPlannerWeeksList.filter(
      (pw) => pw.id !== updatedPlannerWeek.id,
    );
    updatedPlannerWeeksList.push(updatedPlannerWeek);

    const mealIDs = plannerDay.entries
      .filter(
        (e) =>
          !e.plannerEntryType || e.plannerEntryType === PlannerEntryType.MEAL,
      )
      .map((e) => e.mealID);
    const noteIDs = plannerDay.entries
      .filter((e) => e.plannerEntryType === PlannerEntryType.NOTE)
      .map((e) => e.objectID);
    const contentEntryIDs = plannerDay.entries
      .filter((e) => e.plannerEntryType === PlannerEntryType.CONTENT_ENTRY)
      .map((e) => e.objectID);
    if (mealIDs.length > 0) {
      mealBasketMealIDsToRemove.push(...mealIDs);
      promises.push(
        ...mealIDs.map((mealID) =>
          deleteMeal({ id: mealID, parentID: updatedPlannerWeek.id }),
        ),
      );
    }
    if (noteIDs.length > 0) {
      promises.push(
        ...noteIDs.map((noteID) =>
          deleteNote({ id: noteID, parentID: updatedPlannerWeek.id }),
        ),
      );
    }
    if (contentEntryIDs.length > 0) {
      promises.push(
        ...contentEntryIDs.map((contentEntryID) =>
          deleteContentEntry({
            id: contentEntryID,
            parentID: updatedPlannerWeek.id,
          }),
        ),
      );
    }
  }
  dispatch(mealsRemovedFromMealBasket(mealBasketMealIDsToRemove));
  const updatePlannerWeekPromises =
    updatedPlannerWeeksList.map(updatePlannerWeek);
  promises.push(...updatePlannerWeekPromises);
  if (promises.length > 0) {
    try {
      await Promise.allSettled(promises);
    } catch (e) {
      console.warn(e);
    }
  }
};

export const plannerCopyPreviousWeekMeals = async (
  plannerViewWeekStartDate,
  fromViewWeekStartDate,
  lazyGetDbPlannerWeek,
  lazyGetFullMealsByParent,
  lazyGetNotesByParent,
  lazyGetContentEntriesByParent,
  createMeal,
  createNote,
  createContentEntry,
  updateMeal,
  updateDbPlannerWeek,
  currentSpaceMembership,
  customerFoodDataID,
) => {
  const pastWeekStartDatesAndDayIndexes =
    dbWeekStartDatesAndDayIndexesCoveringViewWeek(fromViewWeekStartDate);
  const startDatesAndDayIndexes = dbWeekStartDatesAndDayIndexesCoveringViewWeek(
    plannerViewWeekStartDate,
  );

  const [{ data: sourcePlannerWeek }, { data: destinationPlannerWeek }] =
    await Promise.all([
      lazyGetDbPlannerWeek(pastWeekStartDatesAndDayIndexes[0].dbWeekStartDate),
      lazyGetDbPlannerWeek(startDatesAndDayIndexes[0].dbWeekStartDate),
    ]);

  const plannerWeekDays = pastWeekStartDatesAndDayIndexes.map((sdi) => {
    if (sourcePlannerWeek?.startDate !== sdi.dbWeekStartDate) {
      return { dayIndex: sdi.dayIndex };
    }
    return {
      dayIndex: sdi.dayIndex,
      plannerDbWeekStartDate: sdi.dbWeekStartDate,
      parentBoardId: sourcePlannerWeek?.id,
      entries: sourcePlannerWeek?.days[daysArrayIndex(sdi.dayIndex)].entries,
    };
  });

  const destinationWeekDays = startDatesAndDayIndexes.map((sdi) => {
    if (destinationPlannerWeek?.startDate !== sdi.dbWeekStartDate) {
      return { dayIndex: sdi.dayIndex };
    }
    return {
      dayIndex: sdi.dayIndex,
      plannerDbWeekStartDate: sdi.dbWeekStartDate,
      parentBoardId: destinationPlannerWeek?.id,
      entries:
        destinationPlannerWeek?.days[daysArrayIndex(sdi.dayIndex)].entries,
    };
  });

  const [{ data: meals }, { data: notes }, { data: contentEntries }] =
    await Promise.all([
      lazyGetFullMealsByParent(sourcePlannerWeek.id),
      lazyGetNotesByParent(sourcePlannerWeek.id),
      lazyGetContentEntriesByParent(sourcePlannerWeek.id),
    ]);

  const itemCopyRecords = plannerWeekDays.flatMap((day, arrayIndex) => {
    return day.entries.map((plannerEntry) => {
      let sourceObject;
      if (plannerEntry.plannerEntryType === PlannerEntryType.NOTE) {
        sourceObject = notes[plannerEntry.objectID];
      } else if (
        plannerEntry.plannerEntryType === PlannerEntryType.CONTENT_ENTRY
      ) {
        sourceObject = contentEntries[plannerEntry.objectID];
      } else {
        sourceObject = meals[plannerEntry.mealID];
      }
      return {
        dayIndex: day.dayIndex,
        plannerEntryType: plannerEntry.plannerEntryType,
        sourcePlannerDbWeekStartDate: day.plannerDbWeekStartDate,
        sourceMealID: plannerEntry.mealID,
        sourceObjectID: plannerEntry.objectID,
        sourceObject,
        destinationPlannerDbWeekStartDate:
          destinationWeekDays[arrayIndex].plannerDbWeekStartDate,
        destinationBoardId: destinationWeekDays[arrayIndex].parentBoardId,
      };
    });
  });

  const currentHealthProGroup = null;
  const insertedObjects = await Promise.all(
    itemCopyRecords.map(async (itemCopyRecord) => {
      if (itemCopyRecord.plannerEntryType === PlannerEntryType.NOTE) {
        const clonedNote = cloneObject(itemCopyRecord.sourceObject);
        const input = newNoteWithDefaultFields(
          clonedNote,
          itemCopyRecord.destinationBoardId,
          currentSpaceMembership.id,
          null,
        );
        const { data: insertedNote } = await createNote(input);
        return insertedNote;
      }
      if (itemCopyRecord.plannerEntryType === PlannerEntryType.CONTENT_ENTRY) {
        const clonedContentEntry = cloneObject(itemCopyRecord.sourceObject);
        const input = { ...clonedContentEntry };
        input.parentID = itemCopyRecord.destinationBoardId;
        input.groups = null;
        const { data: insertedContentEntry } = await createContentEntry(input);
        return insertedContentEntry;
      }
      const input = newMealWithDefaultFields(
        cloneObject(itemCopyRecord.sourceObject),
        itemCopyRecord.destinationBoardId,
        currentSpaceMembership?.id,
        currentHealthProGroup,
      );
      const { data: insertedMeal } = await createMeal(input);
      return insertedMeal;
    }),
  );
  console.log(
    `itemCopyRecords count=${itemCopyRecords.length}; insertedObjects count=${insertedObjects.length}`,
  );
  itemCopyRecords.forEach((itemCopyRecord, i) => {
    // eslint-disable-next-line no-param-reassign
    itemCopyRecord.destinationObject = insertedObjects[i];
  });
  const updatedPlannerWeeks = {
    [destinationPlannerWeek.startDate]: destinationPlannerWeek,
  };
  itemCopyRecords.forEach((itemCopyRecord) => {
    let updatedPlannerWeek =
      updatedPlannerWeeks[itemCopyRecord.destinationPlannerDbWeekStartDate];
    if (itemCopyRecord.plannerEntryType === 'NOTE') {
      updatedPlannerWeek = plannerWeekWithNoteAdded(
        updatedPlannerWeek,
        itemCopyRecord.destinationObject,
        itemCopyRecord.dayIndex,
      );
    } else {
      updatedPlannerWeek = plannerWeekWithMealAdded(
        updatedPlannerWeek,
        itemCopyRecord.destinationObject,
        itemCopyRecord.dayIndex,
      );
    }
    updatedPlannerWeeks[itemCopyRecord.destinationPlannerDbWeekStartDate] =
      updatedPlannerWeek;
  });
  trackEvents(['Copy planner week']);
  await Promise.all([
    ...Object.values(updatedPlannerWeeks).map(updateDbPlannerWeek),
    ...itemCopyRecords
      .filter(
        (itemCopyRecord) =>
          !itemCopyRecord.plannerEntryType ||
          itemCopyRecord.plannerEntryType === PlannerEntryType.MEAL,
      )
      .map(async (itemCopyRecord) => {
        const { ingsFullText, analyzedIngredients } =
          await analyzeMissingIngredientsOperation(
            itemCopyRecord.destinationObject.recipes[0].ingredients,
            customerFoodDataID,
          );
        if (analyzedIngredients.length > 0) {
          for (let i = 0; i < ingsFullText.length; i += 1) {
            if (analyzedIngredients[i]) {
              analyzedIngredients[i].fullText = ingsFullText[i];
            }
          }
          const updatedMeal = mealWithIngredientTokensUpdated(
            itemCopyRecord.destinationObject,
            analyzedIngredients,
          );
          await updateMeal(updatedMeal);
        }
      }),
  ]);
};
